import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import {
  ForwardedRef,
  Ref,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

type Props = {
  title: string;
  message: string;
  onConfirm: () => void;
  bodyContent?: JSX.Element;
  isLoading?: boolean;
};

export type ConfirmDialogHandle = {
  open: () => void;
  close: () => void;
};

const ConfirmDialog = (
  { title, message, onConfirm, bodyContent, isLoading }: Props,
  ref: ForwardedRef<ConfirmDialogHandle>,
) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  useImperativeHandle(ref, () => ({ open: onOpen, close: onClose }), [
    onClose,
    onOpen,
  ]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {title}
          </AlertDialogHeader>
          <AlertDialogBody>
            {message}
            {bodyContent}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              {t("common.buttons.cancel")}
            </Button>
            <Button
              colorScheme="red"
              onClick={onConfirm}
              ml={3}
              isLoading={isLoading}
            >
              {t("common.buttons.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default forwardRef(ConfirmDialog) as (
  props: Props & { ref?: Ref<ConfirmDialogHandle> },
) => JSX.Element;
